import { useState, useRef, useEffect } from 'react';
import { Zap, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ContactPickerProps {
  linkedContactIds: string[];
  allContacts: Array<{ id: string; name: string; role?: string }>;
  position: { top: number; right: number };
  onSelectExisting: (contactId: string) => void;
  onCreateQuick: (name: string, role: string) => void;
  onCreateAdvanced: () => void;
  onClose: () => void;
}

export function ContactPicker({
  linkedContactIds,
  allContacts,
  position,
  onSelectExisting,
  onCreateQuick,
  onCreateAdvanced,
  onClose,
}: ContactPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [createMode, setCreateMode] = useState<'none' | 'quick'>('none');
  const [quickName, setQuickName] = useState('');
  const [quickRole, setQuickRole] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const available = allContacts.filter(
    (c) => !linkedContactIds.includes(c.id) && c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleQuickSubmit = () => {
    const name = quickName.trim();
    if (!name) return;
    onCreateQuick(name, quickRole.trim());
  };

  return (
    <div
      ref={popoverRef}
      className="fixed w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-[100] p-3 flex flex-col gap-2"
      style={{ top: position.top, right: position.right }}
    >
      <input
        type="text"
        placeholder={t('entity.contact_picker_search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 outline-none focus:border-violet-500/40 transition"
        autoFocus
      />

      <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
        {available.length === 0 ? (
          <p className="text-xs text-zinc-500 p-2 text-center">
            {t('entity.contact_picker_empty')}
          </p>
        ) : (
          available.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelectExisting(c.id)}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-zinc-800 transition-colors w-full text-left cursor-pointer"
            >
              <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center text-[9px] font-medium text-zinc-400 shrink-0">
                {c.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs text-zinc-300 truncate block">{c.name}</span>
                {c.role && (
                  <span className="text-[10px] text-zinc-500 truncate block">{c.role}</span>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      <div className="border-t border-zinc-700/50 mt-2 pt-2">
        {createMode === 'none' ? (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setCreateMode('quick')}
              className="flex items-center gap-2 p-1.5 rounded-lg text-zinc-400 hover:text-violet-400 hover:bg-zinc-800/50 transition-colors w-full text-left cursor-pointer"
            >
              <Zap size={13} />
              <span className="text-xs">{t('entity.contact_picker_quick')}</span>
            </button>
            <button
              type="button"
              onClick={onCreateAdvanced}
              className="flex items-center gap-2 p-1.5 rounded-lg text-zinc-400 hover:text-violet-400 hover:bg-zinc-800/50 transition-colors w-full text-left cursor-pointer"
            >
              <UserPlus size={13} />
              <span className="text-xs">{t('entity.contact_picker_advanced')}</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder={t('entity.contact_name_placeholder')}
              value={quickName}
              onChange={(e) => setQuickName(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 outline-none focus:border-violet-500/40 transition"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSubmit();
                }
              }}
            />
            <input
              type="text"
              placeholder={t('entity.contact_role_placeholder')}
              value={quickRole}
              onChange={(e) => setQuickRole(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 outline-none focus:border-violet-500/40 transition"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSubmit();
                }
              }}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleQuickSubmit}
                disabled={!quickName.trim()}
                className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {t('common.create')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateMode('none');
                  setQuickName('');
                  setQuickRole('');
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
